"use client";

import type React from "react";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { isValidEmail } from "@/lib/utils";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { FORM_SERVICES } from "@/lib/analytics/config";

// The canonical subject list. Every ?subject= deep link on the site resolves to
// one of these, so messages arrive pre-categorised instead of as free text.
const SUBJECT_TOPICS = [
  "General Inquiry",
  "Technical Support",
  "License & Activation",
  "Refund Request",
  "New Tool or Feature Request",
  "Partnerships",
  "Custom Development",
  "Security Vulnerability Report",
] as const;

// Sentinel for the escape hatch. Not a topic — selecting it reveals a free-text
// field, so an unmapped or hand-written subject is still possible.
const OTHER = "Other" as const;

// Older ?subject= values that predate this list, mapped to their new home.
// These URLs are live in the wild — in page copy, and potentially in anything
// a visitor bookmarked or shared — so dropping them would silently downgrade a
// categorised link into "Other". Keep entries here rather than rewriting
// history; the cost is one line per retired label.
const SUBJECT_ALIASES: Record<string, (typeof SUBJECT_TOPICS)[number]> = {
  "Custom Dev": "Custom Development",
  "Activation Help": "License & Activation",
  "Resend my license key": "License & Activation",
};

/**
 * Resolve an incoming ?subject= value to a dropdown entry.
 *
 * Returns the matched topic, or null when nothing matches — the caller then
 * falls back to "Other" and preserves the original text verbatim rather than
 * discarding what the link author intended.
 *
 * Matching is case-insensitive and whitespace-tolerant because these values
 * travel through URLs and get hand-edited.
 */
function resolveSubject(raw: string): (typeof SUBJECT_TOPICS)[number] | null {
  const needle = raw.trim().toLowerCase();
  if (!needle) return null;

  const exact = SUBJECT_TOPICS.find((t) => t.toLowerCase() === needle);
  if (exact) return exact;

  const aliased = Object.entries(SUBJECT_ALIASES).find(([k]) => k.toLowerCase() === needle);
  if (aliased) return aliased[1];

  return null;
}

export default function ContactForm() {
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Tracks whether form_start has already fired, so it counts once per
  // session rather than on every keystroke.
  const formStarted = useRef(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  // Free-text subject, used only while formData.subject === OTHER. Kept
  // separate so switching to "Other" and back does not destroy what the
  // visitor typed, and so an unmapped ?subject= survives round-tripping.
  const [customSubject, setCustomSubject] = useState("");

  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") return;

    const subjectParam = searchParams.get("subject");
    const messageParam = searchParams.get("message");
    if (subjectParam || messageParam) {
      // An incoming subject that matches a topic (directly or by alias) selects
      // it; anything else falls to "Other" with the original text preserved.
      // ProductPageLayout's per-product link is the reason this fallback has to
      // keep the raw value rather than discarding it.
      const resolved = subjectParam ? resolveSubject(subjectParam) : null;
      if (subjectParam && !resolved) setCustomSubject(subjectParam);

      setFormData((prev) => ({
        ...prev,
        ...(subjectParam && { subject: resolved ?? OTHER }),
        ...(messageParam && { message: messageParam }),
      }));
      // Scroll to form container after a brief delay to ensure it's rendered
      setTimeout(() => {
        const formContainer = document.getElementById("contact-form-container");
        if (formContainer) {
          // Calculate offset to account for fixed header (approximately 128px based on pt-32)
          const headerOffset = 128;
          const elementTop = formContainer.offsetTop;
          const offsetPosition = elementTop - headerOffset;

          window.scrollTo({
            top: Math.max(0, offsetPosition), // Ensure we don't scroll to negative position
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [searchParams]);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null; // Or a loading skeleton
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "email" && emailError) {
      setEmailError("");
    }

    // First interaction with any field marks the form as started.
    if (!formStarted.current) {
      formStarted.current = true;
      window.trackFormInteraction?.("start", {
        form_id: "contact_form",
        form_source: "contact_page",
      });
    }
  };

  // What actually gets sent. "Other" is a UI sentinel, never a subject line —
  // submitting it verbatim would label every custom enquiry "Other" and lose
  // what the visitor typed.
  const effectiveSubject = formData.subject === OTHER ? customSubject.trim() : formData.subject;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidEmail(formData.email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: FORM_SERVICES.WEB3FORMS_ACCESS_KEY,
          from_name: "PlugMonkey Contact Form",
          ...formData,
          subject: effectiveSubject,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // A contact enquiry is a genuine lead. The subject doubles as
        // lead_source so "Custom Development" and "Refund Request" separate in
        // GA4 without needing extra dimensions.
        const leadSource =
          effectiveSubject === OTHER
            ? "other"
            : effectiveSubject.toLowerCase().replace(/[^a-z0-9]+/g, "_");

        window.trackFormInteraction?.("submit", {
          form_id: "contact_form",
          form_source: leadSource,
        });

        window.trackLead?.({
          form_name: "contact_form",
          form_id: "contact_form",
          form_source: "contact_page",
          lead_source: leadSource,
          // Sent to Google only as a SHA-256 hash (lib/analytics/user-data.ts)
          email: formData.email,
        });

        toast({
          title: "Message sent!",
          description: "We'll get back to you as soon as possible.",
        });
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
        setCustomSubject("");
      } else {
        toast({
          title: "Something went wrong",
          description: "Please try again later or contact us directly.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="contact-form"
      onSubmit={handleSubmit}
      className="space-y-6 bg-background/50 p-8 rounded-[2rem] border border-border/50 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name</Label>
          <Input
            id="name"
            name="name"
            placeholder="John Doe"
            required
            value={formData.name}
            onChange={handleChange}
            autoComplete="name"
            className="h-12 rounded-xl border-2 focus:border-primary/20"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="john@example.com"
            required
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
            aria-describedby={emailError ? "contact-email-error" : undefined}
            aria-invalid={!!emailError}
            className={`h-12 rounded-xl border-2 focus:border-primary/20 ${emailError ? "border-red-500" : ""}`}
          />
          {emailError && (
            <p id="contact-email-error" className="text-sm text-red-500" role="alert">
              {emailError}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="subject">Subject</Label>
        {/* A real <select> so messages arrive pre-categorised. Every ?subject=
            deep link on the site resolves to one of these options, directly or
            through SUBJECT_ALIASES. "Other" stays as a deliberate escape hatch
            for anything unmapped — including a bookmarked old link — so a
            prefill is never silently discarded. */}
        <select
          id="subject"
          name="subject"
          required
          value={formData.subject}
          onChange={handleChange}
          className="flex h-12 w-full rounded-xl border-2 border-input bg-background px-3 py-1 text-sm transition-colors focus:border-primary/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="" disabled>
            Choose a subject…
          </option>
          {SUBJECT_TOPICS.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
          <option value={OTHER}>{OTHER}</option>
        </select>
      </div>

      {formData.subject === OTHER && (
        <div className="space-y-2">
          <Label htmlFor="customSubject">What is it about?</Label>
          <Input
            id="customSubject"
            name="customSubject"
            required
            placeholder="Briefly describe your subject"
            value={customSubject}
            onChange={(e) => setCustomSubject(e.target.value)}
            autoComplete="off"
            className="h-12 rounded-xl border-2 focus:border-primary/20"
          />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="message">Your Message</Label>
        <Textarea
          id="message"
          name="message"
          placeholder="Enter your message here..."
          rows={6}
          required
          value={formData.message}
          onChange={handleChange}
          autoComplete="off"
          className="rounded-xl border-2 focus:border-primary/20"
        />
      </div>
      <Button
        type="submit"
        className="w-full h-14 rounded-full text-lg font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all hover:-translate-y-0.5"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        By submitting this form, you agree to our{" "}
        <Link href="/privacy-policy/" className="underline underline-offset-2">
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link href="/terms-of-service/" className="underline underline-offset-2">
          Terms of Service
        </Link>
        .
      </p>
    </form>
  );
}
